import { Linkedin, Github, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';

import logo from '@/assets/logo.webp';
import logo2x from '@/assets/logo@2x.webp';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string; className?: string }>;

function XIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

interface NavItem { label: string; path: string; }
interface SocialLink { icon: IconComponent; href: string; label: string; }

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Work', path: '/work' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

const SOCIAL_LINKS: SocialLink[] = [
  { icon: Linkedin as IconComponent, href: 'https://linkedin.com/company/smartkubo', label: 'LinkedIn' },
  { icon: Github as IconComponent, href: 'https://github.com/smartkubo', label: 'GitHub' },
  { icon: XIcon as IconComponent, href: 'https://x.com/smartkubo_hq', label: 'X' },
  { icon: Facebook as IconComponent, href: 'https://facebook.com/smartkubo', label: 'Facebook' },
];

const LEGAL_LINKS: NavItem[] = [
  { label: 'Privacy Policy', path: '/privacy' },
  { label: 'Terms of Service', path: '/terms' },
];

export function Footer(): React.ReactElement {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-surface-dark text-surface-dark-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-end gap-2 mb-4">
              <img src={logo} srcSet={`${logo} 1x, ${logo2x} 2x`} alt="Smartkubo logo" width={28} height={32} className="h-8 w-auto" />
              <span className="text-2xl font-bold leading-none tracking-wide">Smartkubo</span>
            </Link>
            <p className="opacity-60 mb-4">Quality software, delivered fast</p>
            <address className="not-italic opacity-40 mb-2">Tabla, Liloan, Cebu, Philippines</address>
            <a href="tel:+63323401719" className="opacity-40 hover:opacity-100 transition-opacity block mb-2">(032) 340 1719</a>
            <a href="mailto:contact@smartkubo.com" className="opacity-40 hover:opacity-100 transition-opacity block">contact@smartkubo.com</a>
          </div>

          {/* Navigation */}
          <div>
            <h2 className="text-lg font-semibold mb-4" id="footer-nav-heading">Navigation</h2>
            <nav className="flex flex-col gap-2" aria-labelledby="footer-nav-heading">
              {NAV_ITEMS.map((item) => (
                <Link key={item.path} to={item.path} className="opacity-40 hover:opacity-100 transition-opacity">
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Social */}
          <div>
            <h2 className="text-lg font-semibold mb-4" id="footer-social-heading">Connect</h2>
            <div className="flex gap-2" aria-label="Social media links">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg opacity-60 hover:opacity-100 hover:bg-white/10 hover:scale-110 hover:-translate-y-0.5 transition-all"
                >
                  <social.icon className="size-6" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="opacity-60 text-sm">© {currentYear} Smartkubo. All rights reserved.</p>
          <div className="flex gap-6">
            {LEGAL_LINKS.map((link) => (
              <Link key={link.path} to={link.path} className="opacity-60 hover:opacity-100 transition-opacity text-sm">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
